import {decodeJwtPayload, normalizeTenantId} from '@utils/tenant';
import type {ImpersonationMode, JwtPortalClaims, PortalUser} from '../types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const normalizeEmpresaId = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = String(value).trim();

    if (!normalized) {
        return null;
    }

    if (UUID_RE.test(normalized)) {
        return normalized.toLowerCase();
    }

    if (/^\d+$/.test(normalized)) {
        return normalized;
    }

    return null;
};

export const getJwtPortalClaims = (token: string | null | undefined): JwtPortalClaims | null => {
    if (!token) {
        return null;
    }

    const payload = decodeJwtPayload(token);

    if (!payload || typeof payload !== 'object') {
        return null;
    }

    return payload as JwtPortalClaims;
};

export const getEmpresaIdFromJwt = (token: string | null | undefined): string | null => {
    const claims = getJwtPortalClaims(token);
    return normalizeEmpresaId(claims?.empresa_id);
};

export const getEmpresaIdFromUser = (user: PortalUser | null | undefined): string | null => {
    if (!user) {
        return null;
    }

    return normalizeEmpresaId(user.empresa_id);
};

export const getPortalPermissions = (
    user: PortalUser | null | undefined,
    token?: string | null
): string[] => {
    const fromUser = Array.isArray(user?.permissions) ? user!.permissions! : [];
    const claims = getJwtPortalClaims(token ?? null);
    const fromJwt = claims && Array.isArray(claims.permissions) ? claims.permissions : [];

    return [...new Set([...fromUser, ...fromJwt].map(p => String(p).trim()).filter(Boolean))];
};

export const getImpersonationMode = (token: string | null | undefined): ImpersonationMode | null => {
    const mode = getJwtPortalClaims(token)?.impersonation_mode;
    return mode === 'A' || mode === 'B' ? mode : null;
};

export const getImpersonatedBy = (token: string | null | undefined): string | null => {
    const value = getJwtPortalClaims(token)?.impersonated_by;
    return value ? String(value).trim() : null;
};

/** Sesión iniciada por un SuperAdmin en modo vista (JWT con `impersonated_by`). */
export const isImpersonatedSession = (token: string | null | undefined): boolean =>
    Boolean(getImpersonatedBy(token));

/** Portal accesible solo con impersonación completa (Modo A) y empresa en contexto. */
/** JWT con contexto completo para llamar `/portal-cliente/*` (tenant + empresa). */
export const hasCompletePortalContext = (token: string | null | undefined): boolean => {
    const claims = getJwtPortalClaims(token);
    return Boolean(normalizeTenantId(claims?.tenant_id) && normalizeEmpresaId(claims?.empresa_id));
};

export const isImpersonationPortalReady = (token: string | null | undefined): boolean => {
    if (!isImpersonatedSession(token)) {
        return false;
    }

    const mode = getImpersonationMode(token);
    const empresaId = getEmpresaIdFromJwt(token);

    return mode === 'A' && Boolean(empresaId);
};
